import { useEffect } from "react";
import { Button, Form, InputNumber, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useGetDealStagesQuery } from "@/redux/rtk/features/CRM/dealStage/dealStageApi";
import { loadAllStaff, clearUserList } from "@/redux/rtk/features/hrm/user/userSlice";
import { loadAllAgent, clearAgent } from "@/redux/rtk/features/CRM/agent/agentSlice";
import { useBulkUpdateDealDetailsMutation } from "@/redux/rtk/features/CRM/deal/dealApi";
import UserPrivateComponent from "@/components/PrivacyComponent/UserPrivateComponent";

export default function BulkUpdateDealDetails({
  selectedRowKeys,
  onClose,
  clearSelection,
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [bulkUpdateDealDetails, { isLoading }] = useBulkUpdateDealDetailsMutation();

  const { data: dealStages, isLoading: dealStageLoading } = useGetDealStagesQuery();
  const { list: staffList } = useSelector((state) => state.users);
  const { list: agentList } = useSelector((state) => state.agent);

  useEffect(() => {
    dispatch(loadAllStaff({ query: "all" }));
    dispatch(loadAllAgent());
    return () => {
      dispatch(clearUserList());
      dispatch(clearAgent());
    };
  }, [dispatch]);

  const onFinish = async (values) => {
    const payload = {
      ids: selectedRowKeys,
      ...values
    };

    // Filter out undefined/null values
    const cleanPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== undefined && v !== null)
    );

    const resp = await bulkUpdateDealDetails(cleanPayload);
    if (resp.data) {
      form.resetFields();
      clearSelection();
      onClose();
    }
  };

  return (
    <UserPrivateComponent permission="update-deal">
      <div className="mt-5">
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          className="flex flex-col gap-4"
        >
          <Form.Item label={t("deal.dealOwner")} name="dealOwnerId">
            <Select
              showSearch
              placeholder={t("deal.selectOwner")}
              allowClear
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={(() => {
                const currentList = Array.isArray(staffList) ? [...staffList] : [];
                const currentUserId = localStorage.getItem("id");
                const currentUserName = localStorage.getItem("user");

                if (currentUserId && !currentList.some(m => String(m.id) === String(currentUserId))) {
                  currentList.push({ id: parseInt(currentUserId), username: currentUserName || `User ${currentUserId}` });
                }

                return currentList.map((user) => ({
                  value: user.id,
                  label: user?.username || (user.firstName || user.lastName ? `${user.firstName} ${user.lastName}` : null) || `User ${user.id}`,
                }));
              })()}
            />
          </Form.Item>

          <Form.Item label={t("deal.stage")} name="dealStageId">
            <Select
              loading={dealStageLoading}
              placeholder={t("deal.selectStatus")}
              allowClear
              options={dealStages?.map(stage => ({
                value: stage.id,
                label: stage.stageName,
              }))}
            />
          </Form.Item>


          <Form.Item label={t("deal.agent")} name="agentId">
            <Select
              showSearch
              placeholder={t("deal.selectAgent")}
              allowClear
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={agentList?.map(agent => ({
                value: agent.id,
                label: agent.companyName,
              }))}
            />
          </Form.Item>

          <Form.Item label={t("deal.agentCommission")} name="agentCommissionPaid">
            <Select placeholder={t("deal.selectStatus")} allowClear>
              <Select.Option value="yes">{t("common.yes")}</Select.Option>
              <Select.Option value="no">{t("common.no")}</Select.Option>
            </Select>
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t("common.cancel")}</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {t("common.update")}
            </Button>
          </div>
        </Form>
      </div>
    </UserPrivateComponent>
  );
}
